// v2 polar bear — carries the bulk in three overlapping masses — heavy haunches,
// a deep mid-body and a tall shoulder hump — slung over thick columnar legs with
// big flared snow paws, plus a low-set neck driving the long Roman-nosed head
// forward, small round ears and a stub tail. Two coats: ice cream-white and a
// warmer ivory. Ported 1:1 from age-of-mcp's v2/polar-bear.ts, using the
// library's shared `critter` (quadLegs) and `wildland` (eyes, tube) helpers.
import * as THREE from "three";
import { defineModel, critter, wildland, type Part } from "../../lib";

const COATS = [
  { fur: "#f3f4f0", shade: "#e1e3dd", snout: "#e9eae4", paw: "#e6e8e2" },
  { fur: "#f2efe2", shade: "#e0dcc9", snout: "#e8e4d4", paw: "#e4e0d0" },
];

function polarBearParts(v: number): Part[] {
  const c = COATS[v % COATS.length]!;
  const parts: Part[] = critter.quadLegs({
    dx: 0.078, dz: 0.045, bodyY: 0.115, rTop: 0.02, rFoot: 0.017,
    color: c.shade, hoof: { color: c.paw, r: 0.021, h: 0.016 },
  });
  parts.push(
    // the bulk: haunches, mid-body, shoulder hump, heavy belly line
    { geometry: new THREE.IcosahedronGeometry(0.062, 0), color: c.fur,
      position: [-0.06, 0.115, 0], scale: [1.1, 1.0, 1.05] },
    { geometry: new THREE.IcosahedronGeometry(0.066, 0), color: c.fur,
      position: [0, 0.118, 0], scale: [1.35, 1.0, 1.0] },
    { geometry: new THREE.IcosahedronGeometry(0.05, 0), color: c.fur,
      position: [0.07, 0.142, 0], scale: [1.0, 1.05, 0.95] },
    { geometry: new THREE.IcosahedronGeometry(0.05, 0), color: c.shade,
      position: [0.005, 0.095, 0], scale: [1.45, 0.8, 0.95] },
    // low-slung neck driving the head forward and down
    wildland.tube([0.1, 0.135, 0], [0.132, 0.122, 0], 0.029, 0.024, c.fur),
    { geometry: new THREE.IcosahedronGeometry(0.032, 0), color: c.fur,
      position: [0.148, 0.118, 0], scale: [1.3, 0.95, 0.9] },
    // long Roman snout + black nose
    { geometry: new THREE.ConeGeometry(0.0145, 0.038, 5), color: c.snout,
      position: [0.185, 0.108, 0], rotation: [0, 0, -Math.PI / 2] },
    { geometry: new THREE.IcosahedronGeometry(0.0065, 0), color: "#16140f",
      position: [0.205, 0.106, 0] },
    // stub tail
    { geometry: new THREE.IcosahedronGeometry(0.011, 0), color: c.fur,
      position: [-0.125, 0.13, 0] },
  );
  for (const s of [1, -1] as const) {
    parts.push({ geometry: new THREE.IcosahedronGeometry(0.0085, 0),
      color: c.shade, position: [0.138, 0.146, s * 0.022] });
  }
  parts.push(...wildland.eyes(0.165, 0.128, 0.0145, 0.0035, "#16130e"));
  return parts;
}

export const polarBearV2 = defineModel({
  id: "polar-bear-v2",
  scale: 2.72, // real-world size correction (see Model.scale)
  batchId: "fable",
  name: "Polar bear (v2)",
  tags: ["creature","polar","bear","low-poly","age-of-mcp","fable"],
  summary: "Low-poly polar bear.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.18,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => polarBearParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => polarBearParts(1) },
  ],
  animations: [],
});
